import React from 'react';
import { Box, Button, Flex, Text } from '@chatsdet/ui';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { CaretDown, CaretRight, Copy, CheckCircle } from '@phosphor-icons/react';

type BodyViewerProps = {
  body: string | null;
  title?: string;
  defaultExpanded?: boolean;
  maxCollapsedHeight?: number;
};

const TRUNCATION_MARKER = '... [truncated]';
const MAX_BODY_LENGTH = 100 * 1024; // 100KB

function formatBody(body: string): { formatted: string; isJson: boolean } {
  try {
    const parsed = JSON.parse(body);
    return { formatted: JSON.stringify(parsed, null, 2), isJson: true };
  } catch {
    return { formatted: body, isJson: false };
  }
}

export function BodyViewer({
  body,
  title,
  defaultExpanded = false,
  maxCollapsedHeight = 200,
}: BodyViewerProps) {
  const [isExpanded, setIsExpanded] = React.useState(defaultExpanded);
  const [copied, setCopied] = React.useState(false);

  if (!body) {
    return (
      <Text size="xs" className="text-muted-foreground italic">
        No body
      </Text>
    );
  }

  const isTruncated = body.endsWith(TRUNCATION_MARKER) || body.length >= MAX_BODY_LENGTH;
  const { formatted, isJson } = formatBody(body.replace(TRUNCATION_MARKER, ''));

  const handleCopy = async () => {
    await navigator.clipboard.writeText(body);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Flex direction="column" gap="xs">
      {title && (
        <Flex align="center" justify="between">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            {isExpanded ? <CaretDown size={12} /> : <CaretRight size={12} />}
            <span className="font-medium">{title}</span>
            {isTruncated && <span className="ml-1 text-warning text-[10px]">(truncated)</span>}
          </button>
          <Button variant="ghost" size="sm" onClick={handleCopy} className="px-2">
            {copied ? <CheckCircle size={14} className="text-success" /> : <Copy size={14} />}
          </Button>
        </Flex>
      )}
      {(isExpanded || !title) && (
        <Box
          className="rounded-md overflow-auto"
          style={{ maxHeight: isExpanded ? undefined : maxCollapsedHeight }}
        >
          <SyntaxHighlighter
            language={isJson ? 'json' : 'text'}
            style={vscDarkPlus}
            customStyle={{
              margin: 0,
              padding: '0.75rem',
              fontSize: '11px',
              borderRadius: '0.375rem',
            }}
            wrapLongLines
          >
            {formatted}
          </SyntaxHighlighter>
        </Box>
      )}
    </Flex>
  );
}
